import { useEffect, useRef, useState, useCallback } from 'react';
import stompSprite from '../Moves/Stomp.png';
import './StompAnimation.css';

/**
 * PROFESSIONAL STOMP ANIMATION - Normal-type Move
 * Based on successful Karate Chop and Bone Club implementation patterns
 * 
 * Animation Sequence:
 * 1. PREPARATION: Player rears back for powerful stomp
 * 2. FOOT_RAISE: frameIndex=0 shows foot raised high
 * 3. STOMP_IMPACT: frameIndex=1 shows devastating ground impact
 * 4. SHOCKWAVE: Ground tremor effects spread outward
 * 5. FADE_OUT: Clean disappearance with settling dust
 * 
 * Sprite: Stomp.png (384x192, 2 frames of 190x192 each)
 * - Frame 0: Raised foot preparing to stomp
 * - Frame 1: Impact crater and shockwave effect
 */
export default function StompAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);
  const [animationPhase, setAnimationPhase] = useState('idle');

  const transitionTo = useCallback((phase) => {
    setAnimationPhase(phase);
  }, []);

  useEffect(() => {
    if (!playerRef?.current || !opponentRef?.current) return;

    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    const container = playerEl.closest('.battle-container') || document.body;

    // Get positioning data
    const p = playerEl.getBoundingClientRect();
    const o = opponentEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // SPRITE CONFIGURATION - Stomp.png (384x192, 2 frames of 190x192 each)
    const SPRITE_CONFIG = {
      frameWidth: 190,   // Width of each frame (190x192)
      frameHeight: 192,  // Height of sprite
      raiseFrame: 0,     // frameIndex=0 (raised foot)
      impactFrame: 1,    // frameIndex=1 (impact crater)
    };

    // Calculate positioning - Stomp targets the opponent directly
    const targetX = o.left - containerRect.left + o.width / 2;
    const targetY = o.top - containerRect.top + o.height / 2; // Center of opponent for impact

    // TIMING CONFIGURATION - Enhanced for dramatic stomp impact
    const TIMING = {
      preparation: 150,    // Player rears back preparation
      footRaise: 200,      // Foot raised high in air
      stompImpact: 350,    // Devastating ground impact
      shockwave: 250,      // Tremor effects spread
      fadeOut: 200,        // Dust settles and cleanup
    };

    console.log('🦶 Stomp Animation - Normal-type Move Starting');
    console.log(`🦶 Target ground position: (${targetX}, ${targetY})`);

    // PHASE 1: PREPARATION (0-150ms)
    setAnimationPhase('preparation');
    playerEl.style.transition = 'transform 150ms ease-in-out';
    playerEl.style.transform = 'scale(1.08) rotate(-1deg) translateY(-5px)'; // Rear back for power
    
    // Subtle ground preparation effect
    container.classList.add('stomp-ground-prepare');
    
    // PHASE 2: FOOT RAISE - frameIndex=0 (150-350ms)
    const footRaiseTimer = setTimeout(() => {
      setAnimationPhase('foot_raise');
      console.log('🦶 Phase 2: Foot Raise (frameIndex=0)');
      
      // Create raised foot effect - frameIndex=0
      const footEffect = document.createElement('div');
      footEffect.className = 'stomp-foot-raise';
      
      // Position above target area for dramatic height
      const footX = targetX - SPRITE_CONFIG.frameWidth / 2;
      const footY = targetY - SPRITE_CONFIG.frameHeight - 40; // High above ground
      
      footEffect.style.left = `${footX}px`;
      footEffect.style.top = `${footY}px`;
      footEffect.style.width = `${SPRITE_CONFIG.frameWidth}px`;
      footEffect.style.height = `${SPRITE_CONFIG.frameHeight}px`;
      
      // Set to frameIndex=0 (raised foot)
      footEffect.style.backgroundImage = `url(${stompSprite})`;
      footEffect.style.backgroundSize = '384px 192px'; // Full sprite sheet
      footEffect.style.backgroundPosition = '0px 0px'; // frameIndex=0 (first 190px)
      footEffect.style.backgroundRepeat = 'no-repeat';
      
      container.appendChild(footEffect);
      animRef.current = footEffect;
      
      // Player shows effort of raising foot
      playerEl.style.transform = 'scale(1.1) rotate(-2deg) translateY(-8px)';
      
      console.log(`🦶 Foot raised at position (${footX}, ${footY})`);
      
    }, TIMING.preparation);
    
    // PHASE 3: STOMP IMPACT - frameIndex=1 (350-700ms)
    const stompImpactTimer = setTimeout(() => {
      setAnimationPhase('stomp_impact');
      console.log('🦶 Phase 3: Devastating Stomp Impact (frameIndex=1)');
      
      if (animRef.current) {
        // Transition to frameIndex=1 with ground impact positioning
        animRef.current.style.backgroundPosition = '-190px 0px'; // frameIndex=1 (second 190px frame)
        animRef.current.className = 'stomp-impact-crater';
        
        // Move to target center for direct impact
        const impactX = targetX - SPRITE_CONFIG.frameWidth / 2;
        const impactY = targetY - SPRITE_CONFIG.frameHeight / 2;
        animRef.current.style.left = `${impactX}px`;
        animRef.current.style.top = `${impactY}px`;
        animRef.current.style.transition = 'all 100ms ease-in'; // Quick slam down
        
        console.log('🦶 Switched to frameIndex=1 (impact crater) - SLAM!');
        
        // Massive impact flash effect
        const impactFlash = document.createElement('div');
        impactFlash.className = 'stomp-impact-flash';
        impactFlash.style.left = `${targetX}px`;
        impactFlash.style.top = `${targetY}px`;
        container.appendChild(impactFlash);
        
        // Screen shake for devastating impact
        container.classList.add('stomp-screen-shake');
        
        // Opponent hit reaction with ground tremor
        opponentEl.classList.add('stomp-ground-hit');
        
        // Clean up flash after animation
        setTimeout(() => {
          if (impactFlash.parentNode) {
            impactFlash.parentNode.removeChild(impactFlash);
          }
        }, 300);
      }
      
    }, TIMING.preparation + TIMING.footRaise);
    
    // PHASE 4: SHOCKWAVE SPREAD (700-950ms)
    const shockwaveTimer = setTimeout(() => {
      setAnimationPhase('shockwave');
      console.log('🦶 Phase 4: Shockwave Tremor Effects');
      
      // Create expanding shockwave rings
      for (let i = 0; i < 3; i++) {
        setTimeout(() => {
          const shockRing = document.createElement('div');
          shockRing.className = 'stomp-shockwave-ring';
          shockRing.style.left = `${targetX}px`;
          shockRing.style.top = `${targetY}px`;
          shockRing.style.animationDelay = `${i * 50}ms`;
          container.appendChild(shockRing);
          
          // Clean up ring after animation
          setTimeout(() => {
            if (shockRing.parentNode) {
              shockRing.parentNode.removeChild(shockRing);
            }
          }, 800);
        }, i * 60);
      }
      
      // Dust cloud effects
      const dustCloud = document.createElement('div');
      dustCloud.className = 'stomp-dust-cloud';
      dustCloud.style.left = `${targetX}px`;
      dustCloud.style.top = `${targetY}px`;
      container.appendChild(dustCloud);
      
      // Clean up dust after animation
      setTimeout(() => {
        if (dustCloud.parentNode) {
          dustCloud.parentNode.removeChild(dustCloud);
        }
      }, 400);
      
    }, TIMING.preparation + TIMING.footRaise + TIMING.stompImpact);
    
    // PHASE 5: FADE OUT (950-1150ms)
    const fadeOutTimer = setTimeout(() => {
      setAnimationPhase('fade_out');
      console.log('🦶 Phase 5: Dust Settles and Fade Out');
      
      if (animRef.current) {
        animRef.current.className = 'stomp-fade-out';
      }
      
      // Remove screen effects
      container.classList.remove('stomp-screen-shake', 'stomp-ground-prepare');
      
    }, TIMING.preparation + TIMING.footRaise + TIMING.stompImpact + TIMING.shockwave);
    
    // PHASE 6: COMPLETE (1150ms)
    const completeTimer = setTimeout(() => {
      setAnimationPhase('complete');
      console.log('🦶 Phase 6: Stomp Animation Complete - Normal-type Move');
      
      // Cleanup and reset
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
        animRef.current = null;
      }
      
      // Reset player and opponent states
      playerEl.style.transition = 'transform 200ms ease-out';
      playerEl.style.transform = '';
      opponentEl.classList.remove('stomp-ground-hit');
      container.classList.remove('stomp-screen-shake', 'stomp-ground-prepare');
      
      onComplete();
      
    }, TIMING.preparation + TIMING.footRaise + TIMING.stompImpact + TIMING.shockwave + TIMING.fadeOut);

    // Enhanced cleanup function
    return () => {
      clearTimeout(footRaiseTimer);
      clearTimeout(stompImpactTimer);
      clearTimeout(shockwaveTimer);
      clearTimeout(fadeOutTimer);
      clearTimeout(completeTimer);
      
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
        animRef.current = null;
      }
      
      // Reset all states
      if (playerEl) {
        playerEl.style.transition = '';
        playerEl.style.transform = '';
      }
      if (opponentEl) {
        opponentEl.classList.remove('stomp-ground-hit');
      }
      if (container) {
        container.classList.remove('stomp-screen-shake', 'stomp-ground-prepare');
      }
      
      console.log('🦶 Stomp Animation: Cleanup completed');
    };
  }, [playerRef, opponentRef, onComplete, transitionTo]);

  return null; // This component only handles DOM manipulation
}

// Set the move type for proper coloring in the battle UI
StompAnimation.moveType = "normal";
